import React, {useMemo, useState} from 'react';
import {Link} from 'react-router-dom';
import '../Games.css';
import {Player} from '../common/types';
import {loadJSON, saveJSON} from '../common/storage';
import PlayerSetup from '../common/PlayerSetup';
import RoundTimer from '../common/RoundTimer';
import CategoryChips from '../common/CategoryChips';
import {useSwipe} from '../common/useSwipe';
import {BASE_NAMES, CUSTOM_CATEGORY, WhoAmIItem} from './whoAmIWords';

const MIN_PLAYERS = 2;
const PLAYERS_KEY = 'games_players';
const CUSTOM_NAMES_KEY = 'whoami_custom_names';
const DISABLED_CATEGORIES_KEY = 'whoami_disabled_categories';
const ROUND_OPTIONS = [45, 60, 90, 120];

type Stage = 'setup' | 'ready' | 'playing' | 'summary';

const WhoAmIGame: React.FC = () => {
    const [players, setPlayers] = useState<Player[]>(() => loadJSON(PLAYERS_KEY, []));
    const [customNames, setCustomNames] = useState<WhoAmIItem[]>(() => loadJSON(CUSTOM_NAMES_KEY, []));
    const [disabledCategories, setDisabledCategories] = useState<string[]>(() => loadJSON(DISABLED_CATEGORIES_KEY, []));
    const [newName, setNewName] = useState('');
    const [roundSeconds, setRoundSeconds] = useState(60);

    const [stage, setStage] = useState<Stage>('setup');
    const [guesserIndex, setGuesserIndex] = useState(0);
    const [currentName, setCurrentName] = useState<WhoAmIItem | null>(null);
    const [usedNames, setUsedNames] = useState<Set<string>>(new Set());
    const [tally, setTally] = useState({correct: 0, skipped: 0});
    const [scores, setScores] = useState<Record<string, number>>({});

    const allNames = useMemo(() => [...BASE_NAMES, ...customNames], [customNames]);
    const categories = useMemo(() => {
        const set = new Set(allNames.map(n => n.category));
        return Array.from(set);
    }, [allNames]);
    const categoryCounts = useMemo(() => {
        const counts: Record<string, number> = {};
        allNames.forEach(n => {
            counts[n.category] = (counts[n.category] || 0) + 1;
        });
        return counts;
    }, [allNames]);
    const activeNames = useMemo(
        () => allNames.filter(n => !disabledCategories.includes(n.category)),
        [allNames, disabledCategories]
    );

    const updatePlayers = (next: Player[]) => {
        setPlayers(next);
        saveJSON(PLAYERS_KEY, next);
    };

    const toggleCategory = (category: string) => {
        const next = disabledCategories.includes(category)
            ? disabledCategories.filter(c => c !== category)
            : [...disabledCategories, category];
        setDisabledCategories(next);
        saveJSON(DISABLED_CATEGORIES_KEY, next);
    };

    const addCustomName = () => {
        const name = newName.trim();
        if (!name) return;
        const next = [...customNames, {name, category: CUSTOM_CATEGORY, custom: true}];
        setCustomNames(next);
        saveJSON(CUSTOM_NAMES_KEY, next);
        setNewName('');
    };

    const removeCustomName = (name: string) => {
        const next = customNames.filter(n => n.name !== name);
        setCustomNames(next);
        saveJSON(CUSTOM_NAMES_KEY, next);
    };

    const pickName = (pool: WhoAmIItem[], used: Set<string>): {item: WhoAmIItem; nextUsed: Set<string>} => {
        let available = pool.filter(n => !used.has(n.name));
        let nextUsed = used;
        if (available.length === 0) {
            available = pool;
            nextUsed = new Set();
        }
        const item = available[Math.floor(Math.random() * available.length)];
        const updatedUsed = new Set(nextUsed);
        updatedUsed.add(item.name);
        return {item, nextUsed: updatedUsed};
    };

    const startGame = () => {
        if (players.length < MIN_PLAYERS || activeNames.length === 0) return;
        const {item, nextUsed} = pickName(activeNames, new Set());
        setGuesserIndex(0);
        setCurrentName(item);
        setUsedNames(nextUsed);
        setTally({correct: 0, skipped: 0});
        setScores(Object.fromEntries(players.map(p => [p.id, 0])));
        setStage('ready');
    };

    const startRound = () => {
        setTally({correct: 0, skipped: 0});
        setStage('playing');
    };

    const advanceWord = () => {
        const {item, nextUsed} = pickName(activeNames, usedNames);
        setCurrentName(item);
        setUsedNames(nextUsed);
    };

    const markGuessed = () => {
        const guesser = players[guesserIndex];
        setTally(t => ({...t, correct: t.correct + 1}));
        setScores(s => ({...s, [guesser.id]: (s[guesser.id] || 0) + 1}));
        advanceWord();
    };

    const markSkipped = () => {
        setTally(t => ({...t, skipped: t.skipped + 1}));
        advanceWord();
    };

    const swipe = useSwipe({
        onSwipeRight: markGuessed,
        onSwipeLeft: markSkipped,
        threshold: 90,
    });

    const finishRound = () => {
        setStage('summary');
    };

    const nextPlayer = () => {
        const {item, nextUsed} = pickName(activeNames, usedNames);
        setCurrentName(item);
        setUsedNames(nextUsed);
        setGuesserIndex(i => (i + 1) % players.length);
        setStage('ready');
    };

    const backToSetup = () => {
        setStage('setup');
        setCurrentName(null);
    };

    const guesser = players[guesserIndex];
    const ranking = useMemo(
        () => players.map(p => ({player: p, score: scores[p.id] || 0})).sort((a, b) => b.score - a.score),
        [players, scores]
    );

    return (
        <div className={`GmPage ${stage === 'setup' ? '' : 'GmPage--noBar'}`}>
            <div className="GmContainer">
                <div className="GmTopBar">
                    <Link to="/games" className="GmBack">← Игры</Link>
                    <h1 className="GmTitle" style={{margin: 0}}>Кто я?</h1>
                </div>

                {stage === 'setup' && (
                    <>
                        <p className="GmSubtitle">
                            Отгадывающий не видит имя на экране — держит телефон экраном к остальным
                            (например, у лба). Все остальные словами описывают персонажа, не называя имя
                            напрямую, пока не закончится время.
                        </p>

                        <PlayerSetup players={players} setPlayers={updatePlayers} minPlayers={MIN_PLAYERS}/>

                        <div className="GmCard">
                            <h3 className="GmCard-title">Время на игрока</h3>
                            <div className="GmBtnRow">
                                {ROUND_OPTIONS.map(sec => (
                                    <button
                                        key={sec}
                                        className={`GmBtn ${roundSeconds === sec ? 'GmBtn--primary' : ''}`}
                                        onClick={() => setRoundSeconds(sec)}
                                    >{sec} сек</button>
                                ))}
                            </div>
                        </div>

                        <div className="GmCard">
                            <h3 className="GmCard-title">Категории ({activeNames.length} персонажей активно)</h3>
                            <CategoryChips
                                categories={categories}
                                counts={categoryCounts}
                                disabled={disabledCategories}
                                onToggle={toggleCategory}
                            />

                            {customNames.length > 0 && (
                                <div className="GmList" style={{marginBottom: 12}}>
                                    {customNames.map(n => (
                                        <div className="GmListItem" key={n.name}>
                                            <span className="GmListItem-name">{n.name}</span>
                                            <button className="GmIconBtn" onClick={() => removeCustomName(n.name)} aria-label="Удалить">✕</button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <div className="GmInputRow">
                                <input
                                    className="GmInput"
                                    placeholder="Своё имя или персонаж"
                                    value={newName}
                                    onChange={(e) => setNewName(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') addCustomName();
                                    }}
                                />
                                <button className="GmBtn" onClick={addCustomName}>Добавить</button>
                            </div>
                        </div>

                        <div className="GmBottomBar">
                            <div className="GmBottomBar-inner">
                                <button
                                    className="GmBtn GmBtn--primary GmBtn--big"
                                    disabled={players.length < MIN_PLAYERS || activeNames.length === 0}
                                    onClick={startGame}
                                >
                                    Начать игру
                                </button>
                            </div>
                        </div>
                    </>
                )}

                {stage === 'ready' && guesser && (
                    <div className="GmPassCard">
                        <p className="GmPassCard-hint">Отгадывает</p>
                        <h2 className="GmPassCard-name">{guesser.name}</h2>
                        <p className="GmHint">
                            Передайте телефон в руки {guesser.name} экраном от себя — {guesser.name} не должен(на)
                            видеть подсказки на экране.
                        </p>
                        <button className="GmBtn GmBtn--primary GmBtn--big" onClick={startRound}>
                            Начать раунд
                        </button>
                    </div>
                )}

                {stage === 'playing' && currentName && (
                    <div
                        className="GmSwipeCard"
                        onTouchStart={swipe.onTouchStart}
                        onTouchMove={swipe.onTouchMove}
                        onTouchEnd={swipe.onTouchEnd}
                        style={swipe.dragging ? {
                            transform: `translateX(${swipe.offsetX}px) rotate(${swipe.offsetX / 20}deg)`,
                            transition: 'none',
                        } : undefined}
                    >
                        <span
                            className="GmSwipeCard-badge GmSwipeCard-badge--right"
                            style={{opacity: swipe.dragging ? Math.min(1, Math.max(0, swipe.offsetX) / 90) : 0}}
                        >УГАДАЛ</span>
                        <span
                            className="GmSwipeCard-badge GmSwipeCard-badge--left"
                            style={{opacity: swipe.dragging ? Math.min(1, Math.max(0, -swipe.offsetX) / 90) : 0}}
                        >ДАЛЬШЕ</span>

                        <h3 className="GmCard-title">{guesser.name} отгадывает</h3>
                        <RoundTimer seconds={roundSeconds} onExpire={finishRound}/>
                        <p className="GmHint">{currentName.category}</p>
                        <p className="GmRoleLocation">{currentName.name}</p>
                        <p className="GmSwipeHint">← смахни, если пропускаем · угадал(а) — смахни вправо →</p>
                        <div className="GmBtnRow" style={{marginTop: 12, justifyContent: 'center'}}>
                            <button className="GmBtn GmBtn--primary" onClick={markGuessed}>Угадал(а) ✅</button>
                            <button className="GmBtn" onClick={markSkipped}>Дальше ⏭</button>
                        </div>
                        <p className="GmHint" style={{marginTop: 12}}>
                            Счёт раунда: {tally.correct} угадано, {tally.skipped} пропущено
                        </p>
                    </div>
                )}

                {stage === 'summary' && guesser && (
                    <div className="GmCard" style={{textAlign: 'center'}}>
                        <h3 className="GmCard-title">Время вышло!</h3>
                        <p>{guesser.name} угадал(а) {tally.correct} из {tally.correct + tally.skipped}</p>

                        <div className="GmRevealList" style={{textAlign: 'left'}}>
                            {ranking.map(r => (
                                <div className="GmRevealItem" key={r.player.id}>
                                    {r.player.name} — {r.score}
                                </div>
                            ))}
                        </div>

                        <div className="GmBtnRow" style={{marginTop: 16, justifyContent: 'center'}}>
                            <button className="GmBtn GmBtn--primary" onClick={nextPlayer}>Следующий игрок</button>
                            <button className="GmBtn" onClick={backToSetup}>Закончить игру</button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default WhoAmIGame;
